import { useState, useEffect, useRef } from 'react'
import { urlScraper } from '../../lib/urlScraper'
import { detectCategory } from '../../lib/categories'
import { classifyItem } from '../../lib/classificationEngine'
import { useContributions } from '../../contexts/ContributionsContext'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Alert, AlertDescription } from '../ui/alert'
import { Card, CardContent, CardHeader } from '../ui/card'
import { StreamlinedProductForm } from '../products/StreamlinedProductForm'
import { BackgroundItemCard } from './BackgroundItemCard'
import {
  Loader2,
  AlertCircle,
  Link
} from 'lucide-react'

interface AddProductProps {
  onSubmit: (formData: any) => Promise<void>
  currentUserId: string
  hideHeader?: boolean
  isAdmin?: boolean
}

export function AddProduct({ onSubmit, currentUserId, isAdmin = false }: AddProductProps) {
  const { cache } = useContributions()
  const [url, setUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scrapedData, setScrapedData] = useState<any>(null)
  const [showProductForm, setShowProductForm] = useState(false)
  const [hasScraped, setHasScraped] = useState(false) // Track if we've attempted scraping for this URL

  // Background scroll animation state
  const columnRefs = useRef<(HTMLDivElement | null)[]>([])
  const [backgroundItems, setBackgroundItems] = useState<any[]>([])

  // Load 30 random items for background
  useEffect(() => {
    if (cache.contributions.length > 0) {
      // Shuffle and take 30 random items
      const shuffled = [...cache.contributions].sort(() => Math.random() - 0.5)
      const items = shuffled.slice(0, 30)
      // Duplicate the items to create seamless infinite scroll
      setBackgroundItems([...items, ...items])
    }
  }, [cache.contributions])

  // Infinite scroll animation for all columns
  useEffect(() => {
    if (backgroundItems.length === 0) return

    let animationFrameId: number
    const scrollPositions = [0, 0, 0, 0, 0, 0, 0] // Support up to 7 columns (2 mobile + 5 desktop)
    // Different speeds for each column to create parallax effect
    const scrollSpeeds = [0.15, 0.25, 0.2, 0.22, 0.18, 0.19, 0.21]

    const animate = () => {
      columnRefs.current.forEach((column, index) => {
        if (!column) return

        // Check if column is visible by checking if it has dimensions
        const rect = column.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return

        scrollPositions[index] += scrollSpeeds[index]

        // Reset position when we've scrolled through half the content
        const halfHeight = column.scrollHeight / 2
        if (halfHeight > 0 && scrollPositions[index] >= halfHeight) {
          scrollPositions[index] = 0
        }

        column.style.transform = `translateY(-${scrollPositions[index]}px)`
      })

      animationFrameId = requestAnimationFrame(animate)
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [backgroundItems])

  // Auto-scrape when URL changes
  useEffect(() => {
    const validateAndScrapeURL = async () => {
      // Skip if no URL, already loading, or already scraped this URL
      if (!url || isLoading || hasScraped) return

      // Normalize URL for validation - add https:// if no protocol
      let testUrl = url.trim()
      if (!testUrl.match(/^https?:\/\//i)) {
        testUrl = 'https://' + testUrl
      }

      // Basic URL validation
      try {
        new URL(testUrl)
      } catch {
        // Not a valid URL yet, user might still be typing
        return
      }

      // Check if URL looks complete (has domain and path)
      if (testUrl.includes('.') && testUrl.length > 10) {
        // Automatically trigger scraping
        handleScrapeURL()
      }
    }
    
    // Debounce the scraping to avoid triggering while user is still typing
    const timer = setTimeout(() => {
      validateAndScrapeURL()
    }, 1500) // Wait 1.5 seconds after user stops typing
    
    return () => clearTimeout(timer)
  }, [url]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scrape product data from URL
  const handleScrapeURL = async () => {
    if (!url) {
      setError('Please enter a product URL')
      return
    }

    // Normalize URL - add https:// if no protocol specified
    let normalizedUrl = url.trim()
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = 'https://' + normalizedUrl
    }

    // Basic URL validation
    try {
      new URL(normalizedUrl)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    // Update the input with the normalized URL
    setUrl(normalizedUrl)

    setIsLoading(true)
    setError(null)
    setHasScraped(true) // Mark that we've attempted scraping for this URL

    try {
      // Don't cache images during initial scraping - just get the data
      // Images will be cached when the contribution is actually created
      const productData = await urlScraper.scrapeProduct(normalizedUrl)
      
      // Prioritize cached image URL
      let imageUrl = productData.cached_image_url || productData.image_url
      if (!productData.cached_image_url && productData.image_url) {
        console.warn('Product scraped without cached image - will cache during creation')
      }
      if (Array.isArray(productData.image_url)) {
        imageUrl = productData.image_url[0] || ''
      }
      
      // Process multiple images if available
      // Note: Image validation, deduplication, and URL normalization is now handled
      // universally in urlScraper.processImages(), so we just need to mark them as selected
      let images = productData.images
      if (images && images.length > 0) {
        // Auto-select first 3 images (images are already validated and deduplicated by scraper)
        // Preserve the highlighted property that comes from the scraper
        images = images.map((img, index) => ({
          ...img,
          selected: index < 3,
          // Keep highlighted property if it exists, otherwise default first image to highlighted
          highlighted: img.highlighted !== undefined ? img.highlighted : index === 0
        }))
      }
      
      // Use LLM classification from scraping if available, otherwise fall back to rule-based
      let detectedCategory = ''
      let detectedSubcategory = ''
      let detectedTags: string[] = []
      let classificationResult: any = null

      // Check if scraper already did LLM classification
      if (productData.category && productData.llm_confidence !== undefined) {
        console.log('Using LLM classification from scraper:', productData.category, '/', productData.subcategory)
        detectedCategory = productData.category
        detectedSubcategory = productData.subcategory || ''
        detectedTags = productData.tags || []
        // Create a classification result object for passing to ProductDetail
        classificationResult = {
          category_name: productData.category,
          subcategory_name: productData.subcategory,
          tags: (productData.tags || []).map((name: string) => ({ tag_name: name })),
          confidence: productData.llm_confidence >= 0.8 ? 'high' : productData.llm_confidence >= 0.5 ? 'medium' : 'low'
        }
      } else {
        // Fall back to rule-based classification
        try {
          const classification = await classifyItem(
            productData.title || '',
            productData.description || '',
            url,
            {
              userId: currentUserId,
              isInitialScrape: true
            }
          )

          // Store full classification result
          classificationResult = classification

          // Map category name to old category enum for backward compatibility
          detectedCategory = classification.category_name || ''
          detectedTags = classification.tags?.map(t => t.tag_name) || []
        } catch (classifyError) {
          console.error('Classification failed during initial scraping:', classifyError)
          // Fallback to old detection if classification fails
          detectedCategory = detectCategory(
            productData.title || '',
            productData.description || '',
            productData.brand || ''
          )
        }
      }

      // Prepare data in the format ProductDetail expects
      const formattedData = {
        title: productData.title || '',
        brand: productData.brand || '',
        description: productData.description || '',
        image_url: imageUrl || '',
        cached_image_url: productData.cached_image_url || '', // Include cached URL
        images: images || [],
        price_amount: productData.price_amount ?? null,
        price_currency: productData.price_currency ?? null,
        category: detectedCategory || '',
        subcategory: detectedSubcategory || '',
        tags: detectedTags.length > 0 ? detectedTags : [],
        impact: productData.impact || '',
        source_url: url,
        comment: '', // New field for "Why do you love this?"
        // Add required fields for ProductDetail component
        id: null, // New product, no ID yet
        created_at: new Date().toISOString(),
        user_id: currentUserId,
        saves_count: 0,
        is_saved: false,
        // Add classification data for ProductDetail to use
        _classification: classificationResult, // Temporary field to pass classification data
        llm_confidence: productData.llm_confidence // Pass confidence for display
      }
      
      setScrapedData(formattedData)
      setShowProductForm(true)
    } catch (err: any) {
      console.error('Scraping error:', err)

      // Use the error message from the Edge Function, or default message
      const errorMessage = err?.message || "We couldn't fetch an image from your URL because access appears to be blocked. Try another URL or upload an image instead."

      setError(errorMessage)
      // Still show form even if scraping fails
      setScrapedData({
        title: '',
        brand: '',
        description: '',
        image_url: '',
        images: [],
        category: '',
        tags: [],
        impact: '',
        source_url: url,
        comment: '',
        id: null,
        created_at: new Date().toISOString(),
        user_id: currentUserId,
        saves_count: 0,
        is_saved: false
      })
      setShowProductForm(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleProductSave = async (productData: any) => {
    // Add the source URL to the product data
    const submissionData = {
      ...productData,
      source_url: url || productData.source_url,
      cached_image_url: productData.image_url,
      images: productData.images?.filter((img: any) => img.selected) || []
    }
    
    // Call the parent's onSubmit handler
    await onSubmit(submissionData)
  }

  const handleSkipScraping = () => {
    // Show form with empty data
    setScrapedData({
      title: '',
      brand: '',
      description: '',
      image_url: '',
      images: [],
      category: '',
      tags: [],
      impact: '',
      source_url: url,
      comment: '',
      id: null,
      created_at: new Date().toISOString(),
      user_id: currentUserId,
      saves_count: 0,
      is_saved: false
    })
    setShowProductForm(true)
  }

  if (showProductForm && scrapedData) {
    return (
      <StreamlinedProductForm
        initialData={scrapedData}
        mode="add"
        onSave={handleProductSave}
        onCancel={() => {
          setShowProductForm(false)
          setScrapedData(null)
        }}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        initialError={error}
      />
    )
  }

  return (
    <div className="fixed inset-0 top-[73px] flex bg-[#F2F0E5] overflow-hidden">
      {/* Animated Background - Scrolling Cards */}
      {backgroundItems.length > 0 && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Grey overlay to desaturate the background */}
          <div className="absolute inset-0 bg-[#F2F0E5]/70 z-10" />

          {/* Scrolling container - full width edge to edge */}
          {/* Mobile: 2 columns, Desktop: 5 columns */}
          <div className="absolute inset-0 flex gap-3 px-3 md:hidden">
            {/* Mobile - 2 columns */}
            {[0, 1].map((columnIndex) => (
              <div
                key={columnIndex}
                className="flex-1"
                style={{
                  opacity: 0.5,
                  filter: 'grayscale(50%)',
                }}
              >
                <div
                  ref={(el) => { columnRefs.current[5 + columnIndex] = el }}
                  className="flex flex-col gap-3"
                >
                  {backgroundItems
                    .filter((_, i) => i % 2 === columnIndex)
                    .map((item, index) => (
                      <BackgroundItemCard key={`${item.id}-${index}-mobile`} item={item} />
                    ))}
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 hidden md:flex gap-3 px-3">
            {/* Desktop - 5 columns */}
            {[0, 1, 2, 3, 4].map((columnIndex) => (
              <div
                key={columnIndex}
                className="flex-1"
                style={{
                  opacity: 0.5,
                  filter: 'grayscale(50%)',
                }}
              >
                <div
                  ref={(el) => { columnRefs.current[columnIndex] = el }}
                  className="flex flex-col gap-3"
                >
                  {backgroundItems
                    .filter((_, i) => i % 5 === columnIndex)
                    .map((item, index) => (
                      <BackgroundItemCard key={`${item.id}-${index}-desktop`} item={item} />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content - Card positioned toward top */}
      <div className="flex-1 flex items-start justify-center relative z-20 p-6 pt-12 md:pt-20">
        <div className="w-full max-w-lg">
          <Card className="border border-gray-200 shadow-2xl bg-white">
            <CardHeader>
              <h2 className="text-2xl font-medium text-center" style={{ fontFamily: 'Canela, Georgia, serif' }}>
                Share with the Community
              </h2>
              <p className="text-center text-gray-600 mt-2" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace", fontSize: '13px' }}>
                Paste a product URL to auto-fill details, or enter manually
              </p>
            </CardHeader>
            <CardContent className="space-y-6 relative">
              {/* Loading Overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#57241F]" />
                    <p className="text-sm font-medium text-gray-700">Extracting product details...</p>
                    <p className="text-xs text-gray-500 mt-1">This may take a few seconds</p>
                  </div>
                </div>
              )}

              {/* URL Input Section */}
              <div className="space-y-4">
                <Label htmlFor="url" className="flex items-center gap-2" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace", fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <Link className="h-4 w-4" />
                  Product URL (Optional)
                </Label>
                <div className="relative">
                  <Input
                    id="url"
                    type="url"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value)
                      // Reset hasScraped if URL is cleared or changed significantly
                      if (!e.target.value || e.target.value.length < 5) {
                        setHasScraped(false)
                        setError(null)
                      }
                    }}
                    placeholder="https://example.com/product"
                    className="pr-12"
                    disabled={isLoading}
                    style={{ fontFamily: "'GT America', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                  />
                  <button
                    onClick={handleScrapeURL}
                    disabled={isLoading || !url}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 transition-all grid place-items-center font-bold text-lg"
                    type="button"
                    style={{ lineHeight: '1' }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <span style={{ marginTop: '-2px' }}>{'>'}</span>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500" style={{ fontFamily: "'GT America', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                  {isLoading ? 'Extracting product details...' : 'Paste a product URL to automatically extract details'}
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription style={{ fontFamily: "'GT America', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {/* Skip to manual entry button */}
              <div className="text-center">
                <Button
                  variant="outline"
                  onClick={handleSkipScraping}
                  disabled={isLoading}
                  className="w-full"
                >
                  Enter Details Manually
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}